import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const DEV_BYPASS = true;
const DEV_USER = {
  id: 666666,
  telegram_id: '66666',
  username: 'Cybor',
  first_name: 'Cybor',
  last_name: 'Cybor',
  status: 'OWNER',
  credits: 9999,
  daily_credits: 100,
  profile_photo_url: '',
  plan: 'LIFETIME',
  created_at: new Date().toISOString(),
};
const DEV_TOKEN = '66';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(DEV_BYPASS ? DEV_USER : null);
  const [loading, setLoading] = useState(DEV_BYPASS ? false : true);
  const [token, setToken] = useState(DEV_BYPASS ? DEV_TOKEN : localStorage.getItem('token'));

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  useEffect(() => {
    const initAuth = async () => {
      if (DEV_BYPASS) {
        setLoading(false);
        return;
      }

      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        try {
          const response = await api.get('/auth/verify');
          if (response.data.valid) {
            setUser(response.data.user);
            setToken(savedToken);
          } else {
            logout();
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(token);
    setUser(userData);
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/user/profile');
      if (response.data.success) {
        const newUserData = response.data.user;
        setUser(newUserData);
        localStorage.setItem('user', JSON.stringify(newUserData));
        return newUserData;
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (DEV_BYPASS || !token || !user) return;

    const pollInterval = setInterval(() => {
      refreshUser().catch(err => console.error('Auto-refresh failed:', err));
    }, 30000);

    const handleRefreshEvent = () => {
      refreshUser();
    };
    window.addEventListener('refreshUser', handleRefreshEvent);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('refreshUser', handleRefreshEvent);
    };
  }, [token, user?.id]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.status === 'ADMIN' || user?.status === 'OWNER',
    isOwner: user?.status === 'OWNER',
    isPremium: ['PREMIUM', 'ADMIN', 'OWNER', 'LIFETIME'].includes(user?.status),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
